import { useCallback } from 'react';

import { isRovoBroadcastConfirmedActions } from '@atlassian/rovo-content-bridge-api-commands/clients/rovo-broadcast-confirmed-action-client';
import { ROVO_CHAT_CLIENT_ID } from '@atlassian/rovo-content-bridge-api-commands/clients/rovo-chat-client';
import type {
	BroadcastableCommandConstructor,
	BroadcastCommandProperties,
	CommandProperties,
} from '@atlassian/rovo-content-bridge-api/command';
import { getBrowserBridgeAPI } from '@atlassian/rovo-content-bridge-api/singleton';

/**
 * React hook that provides a function to broadcast a confirmed action command
 * through the browser bridge API. The broadcasted command will always include the
 * `ROVO_CHAT_CLIENT_ID` as the sender ID, ensuring the command is recognized as
 * originating from the Rovo chat client.
 *
 * The returned `broadcastConfirmedActionCommand` function accepts a command constructor
 * and command parameters, augments the parameters with the required sender ID, and
 * broadcasts the command using the bridge API. The broadcast is filtered to only
 * include confirmed actions relevant to Rovo.
 *
 * @returns An object containing the `broadcastConfirmedActionCommand` function.
 *
 * @template C - The type of command properties.
 * @template R - The type of command result.
 */
export const useBridgeBroadcastActionCommand = (): {
	broadcastConfirmedActionCommand: <C extends CommandProperties>(
		command: BroadcastableCommandConstructor<C>,
		params: BroadcastCommandProperties<C>,
	) => void;
} => {
	const bridgeAPI = getBrowserBridgeAPI();

	const broadcastConfirmedActionCommand = useCallback(
		<C extends CommandProperties>(
			command: BroadcastableCommandConstructor<C>,
			params: BroadcastCommandProperties<C>,
		): void => {
			const paramsWithRovoSenderId = {
				...params,
				senderId: ROVO_CHAT_CLIENT_ID,
			} as C;

			return bridgeAPI.broadcast(command, paramsWithRovoSenderId, {
				filter: isRovoBroadcastConfirmedActions,
			});
		},
		[bridgeAPI],
	);

	return { broadcastConfirmedActionCommand };
};
